import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { enableProdMode, ErrorHandler, inject, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideRouter, Router, RouteReuseStrategy, withComponentInputBinding } from '@angular/router';
import { IonicRouteStrategy, provideIonicAngular } from '@ionic/angular/standalone';
import * as Sentry from '@sentry/angular';
import { provideTanStackQuery, QueryClient } from '@tanstack/angular-query-experimental';
import { withDevtools } from '@tanstack/angular-query-experimental/devtools';

import { AppComponent } from './app/app.component';
import { routes } from './app/app.routes';
import { authInterceptor } from './app/interceptors/auth-interceptor.service';
import { errorInterceptor } from './app/interceptors/error-interceptor.service';
import { timeoutInterceptor } from './app/interceptors/timeout-interceptor.service';
import { tokenRefreshInterceptor } from './app/interceptors/token-refresh-interceptor.service';
import { trackingInterceptor } from './app/interceptors/tracking-interceptor.service';
import { NetworkService } from '@core/services/network/network.service';
import { environment } from './environments/environment';
import { BUILD_INFO } from './environments/build-info';

let networkRef: NetworkService | null = null;

if (environment.production) {
  enableProdMode();
}

if (environment.sentryDsn) {
  const release = `movilidad-tec@${environment.version}-${BUILD_INFO?.commitHash || 'unknown'}`;
  Sentry.init({
    dsn: environment.sentryDsn,
    environment: environment.production ? 'production' : 'development',
    release,
    integrations: [Sentry.browserTracingIntegration(), Sentry.httpClientIntegration()],
    tracesSampleRate: environment.production ? 0.2 : 1.0,
    tracePropagationTargets: [environment.apiBaseUrl],
    sendDefaultPii: true,
    ignoreErrors: [
      // Common browser network noise (Sentry best practice)
      'NetworkError when attempting to fetch resource',
      'The Internet connection appears to be offline',
      'Network request failed',
      'Load failed',
      /^Failed to fetch$/,
      /^Network Error$/,
      /Google Maps API/,
      // Angular HttpClient offline errors (status 0)
      /Http failure response for .+: 0 Unknown Error/,
    ],
    beforeSend(event) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const conn = (navigator as any).connection;
      if (conn) {
        event.contexts = {
          ...event.contexts, connection: {
            effectiveType: conn.effectiveType,
            downlink: conn.downlink,
            rtt: conn.rtt,
            type: conn.type,
          }
        };
      }

      const online = networkRef?.isOnline() ?? navigator.onLine;
      if (!online) {
        const exceptionValue = event.exception?.values?.[0]?.value ?? '';
        if (/0 Unknown Error|Failed to fetch|NetworkError/i.test(exceptionValue)) {
          return null;
        }
      }
      return event;
    },
  });
}

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    provideIonicAngular({
      innerHTMLTemplatesEnabled: true,
      useSetInputAPI: true,
    }),
    provideHttpClient(
      withInterceptors([
        trackingInterceptor,
        timeoutInterceptor,
        authInterceptor,
        tokenRefreshInterceptor,
        errorInterceptor,
      ])
    ),
    provideRouter(routes, withComponentInputBinding()),
    provideTanStackQuery(
      new QueryClient({
        defaultOptions: {
          queries: {
            // Disable TanStack retry — the HTTP interceptor already handles retry
            // with exponential backoff for idempotent methods. Having both would
            // multiply attempts (interceptor 3 × TanStack 3 = 9 server requests).
            retry: false,
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
          },
        },
      }),
      withDevtools()
    ),
    ...(environment.sentryDsn
      ? [
        { provide: ErrorHandler, useValue: Sentry.createErrorHandler({ showDialog: false }) },
        { provide: Sentry.TraceService, deps: [Router] },
        provideAppInitializer(() => {
          inject(Sentry.TraceService);
        }),
      ]
      : []),
  ],
}).then((appRef) => {
  networkRef = appRef.injector.get(NetworkService);
}).catch((err) => {
  console.error('Bootstrap failed:', err);
  const root = document.querySelector('app-root');
  if (root) {
    root.innerHTML = '<div style="padding:2rem;text-align:center;font-family:sans-serif">'
      + '<p>Error al iniciar. Por favor reinicia la aplicaci\u00f3n.</p>'
      + '<button onclick="location.reload()" style="padding:.5rem 1rem;font-size:1rem">Reintentar</button></div>';
  }
});
